import React, { useState } from 'react'
import Layout from '../../components/layout/Layout'
import { useParams, useNavigate } from 'react-router-dom'
import { PencilAltIcon, TrashIcon, XIcon } from "@heroicons/react/solid";
import toast from "react-hot-toast";
import { Modal } from '@mui/material';
import useGet from '../../hooks/read/useGet';
import usePut from '../../hooks/usePut';
import Loader from '../../components/Loader';

const ViewMonthlyAttendance = () => {
  const { financial_year_id, user_id } = useParams();
  const navigate = useNavigate();
  const [editingRow, setEditingRow] = useState(null);
  const [editData, setEditData] = useState({});
  const [deleteConfirmationModal, setDeleteConfirmationModal] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [shouldExitEditMode, setShouldExitEditMode] = useState(false);
  const [shouldCloseDeleteModal, setShouldCloseDeleteModal] = useState(false);

  // Fetch monthly attendance data
  const { list: monthlyAttendanceList } = useGet({
    url: "employee_monthly_attendance_count_list",
    initialData: {
      financial_year_id: financial_year_id,
      user_id: user_id,
      limit: 100,
    },
    onError: () => {
      toast.error("Failed to fetch monthly attendance data");
    }
  });

  // Update attendance mutation
  const { handleAuth: updateAttendance } = usePut({
    url: "update_employee_monthly_attendance",
    refreshUrl: `employee_monthly_attendance_count_list`,
    onSuccess: () => {
      toast.success("Attendance updated successfully");
      setShouldExitEditMode(true);
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || "Failed to update attendance");
      // Revert to original values and close edit mode
      setEditingRow(null);
      setEditData({});
    }
  });

  // Delete attendance mutation
  const { handleAuth: deleteAttendance } = usePut({
    url: "delete_employee_monthly_attendance",
    refreshUrl: `employee_monthly_attendance_count_list`,
    onSuccess: () => {
      toast.success("Attendance record deleted successfully");
      setShouldCloseDeleteModal(true);
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || "Failed to delete attendance");
      // Close delete modal on error
      setDeleteConfirmationModal(false);
    }
  });

  // Exit edit mode when data finishes refetching
  if (shouldExitEditMode && !monthlyAttendanceList.isFetching) {
    setEditingRow(null);
    setEditData({});
    setShouldExitEditMode(false);
  }

  // Close delete modal when data finishes refetching
  if (shouldCloseDeleteModal && !monthlyAttendanceList.isFetching) {
    setDeleteConfirmationModal(false);
    setShouldCloseDeleteModal(false);
  }

  const handleEdit = (idx, data) => {
    if (editingRow === idx) {
      const payload = {
        monthly_attendance_id: data.monthly_attendance_id,
        present_days: editData.present_days,
        absent_days: editData.absent_days,
        total_leaves: editData.total_leaves
      };
      updateAttendance.mutate(payload);
    } else {
      setEditingRow(idx);
      setEditData({
        present_days: data.present_days,
        absent_days: data.absent_days,
        total_leaves: data.total_leaves,
      });
    }
  };

  const handleInputChange = (field, value) => {
    setEditData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleOpenDeleteModal = (rowId) => {
    setSelectedRowId(rowId);
    setDeleteConfirmationModal(true);
  };

  const handleDelete = () => {
    deleteAttendance.mutate({
      monthly_attendance_id: selectedRowId
    });
  };

  const employeeData = monthlyAttendanceList.data?.user_id ? {
    name: monthlyAttendanceList.data?.employee_name,
    code: monthlyAttendanceList.data?.employee_code,
    financialYear: monthlyAttendanceList.data?.financial_year?.name
  } : null;

  return (
    <Layout>
      <div className="px-4 py-6">
        {employeeData && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-8">
            <div className="flex justify-between items-start mb-4">
              <button
                onClick={() => navigate(-1)}
                className="flex items-center hover:underline text-color1 font-semibold"
              >
                ← Back
              </button>
            </div>
            <h1 className="text-2xl font-bold text-color1 mb-4">Monthly Attendance</h1>
            <div className="grid grid-cols-3 gap-8">
              <div>
                <p className="text-gray-600 text-sm font-medium">Employee Name</p>
                <p className="text-lg font-semibold text-gray-900 mt-1">{employeeData.name}</p>
              </div>
              <div>
                <p className="text-gray-600 text-sm font-medium">Employee Code</p>
                <p className="text-lg font-semibold text-gray-900 mt-1">{employeeData.code}</p>
              </div>
              <div>
                <p className="text-gray-600 text-sm font-medium">Financial Year</p>
                <p className="text-lg font-semibold text-gray-900 mt-1">{employeeData.financialYear}</p>
              </div>
            </div>
          </div>
        )}

        {monthlyAttendanceList.isLoading ? (
          <div className="flex justify-center items-center py-12">
            <Loader className="h-8 w-8 text-color1" />
          </div>
        ) : monthlyAttendanceList.data?.data?.length <= 0 ? (
          <div className='text-center my-10'>
            <p className='text-gray-700 text-3xl'>No Data To Show</p>
          </div>
        ) : (
          <>
            <div className="bg-white rounded-lg shadow-md overflow-x-auto">
              <table className="table-auto w-full">
                <thead>
                  <tr className="bg-color1 text-center">
                    <th className="min-w-[150px] text-lg font-semibold text-white py-3 px-4 border-l">Month</th>
                    <th className="min-w-[150px] text-lg font-semibold text-white py-3 px-4">Present Days</th>
                    <th className="min-w-[150px] text-lg font-semibold text-white py-3 px-4">Absent Days</th>
                    <th className="min-w-[150px] text-lg font-semibold text-white py-3 px-4">Total Leaves</th>
                    <th className="min-w-[150px] text-lg font-semibold text-white py-3 px-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {monthlyAttendanceList.data?.data?.map((item, idx) => (
                    <tr key={idx}>
                      <td className="text-center text-dark font-medium text-sm py-3 px-4 bg-[#F3F6FF] border-b border-l">{item.attendance_month_name}</td>
                      <td className="text-center text-dark font-medium text-sm py-3 px-4 bg-[#F3F6FF] border-b">
                        {editingRow === idx ? (
                          <input
                            type="number"
                            value={editData.present_days}
                            onChange={(e) => handleInputChange("present_days", e.target.value)}
                            className="w-20 text-center border rounded"
                          />
                        ) : item.present_days}
                      </td>
                      <td className="text-center text-dark font-medium text-sm py-3 px-4 bg-[#F3F6FF] border-b">
                        {editingRow === idx ? (
                          <input
                            type="number"
                            value={editData.absent_days}
                            onChange={(e) => handleInputChange("absent_days", e.target.value)}
                            className="w-20 text-center border rounded"
                          />
                        ) : item.absent_days}
                      </td>
                      <td className="text-center text-dark font-medium text-sm py-3 px-4 bg-[#F3F6FF] border-b">
                        {editingRow === idx ? (
                          <input
                            type="number"
                            value={editData.total_leaves}
                            onChange={(e) => handleInputChange("total_leaves", e.target.value)}
                            className="w-20 text-center border rounded"
                          />
                        ) : item.total_leaves}
                      </td>
                      <td className="text-center text-dark font-medium text-sm py-3 px-4 bg-[#F3F6FF] border-b">
                        <div className="flex justify-center space-x-2">
                          <button
                            onClick={() => handleEdit(idx, item)}
                            disabled={updateAttendance.isLoading || monthlyAttendanceList.isFetching}
                            className="flex items-center"
                          >
                            {editingRow === idx ? (
                              <div className="flex items-center gap-2">
                                {updateAttendance.isLoading || monthlyAttendanceList.isFetching ? (
                                  <>
                                    <Loader className="h-4 w-4 text-blue-600" />
                                    <span className="text-blue-600 font-semibold text-sm">Saving...</span>
                                  </>
                                ) : (
                                  <span className="text-blue-600 font-semibold text-sm">Save</span>
                                )}
                              </div>
                            ) : (
                              <PencilAltIcon className="text-gray-600 w-5 hover:text-blue-600" />
                            )}
                          </button>
                          <button
                            onClick={() => handleOpenDeleteModal(item.monthly_attendance_id)}
                            disabled={deleteAttendance.isLoading}
                          >
                            <TrashIcon className="text-gray-600 w-5 hover:text-red-600 cursor-pointer" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <Modal open={deleteConfirmationModal}>
        <div className='max-w-lg w-full bg-white absolute left-1/2 -translate-x-1/2 top-[20%] mx-auto rounded-lg shadow-lg'>
          <div className='px-6 py-4 border-b'>
            <div className='flex items-center justify-between'>
              <h3 className='text-gray-900 text-xl font-semibold'>Delete Record</h3>
              <button onClick={() => setDeleteConfirmationModal(false)}>
                <XIcon className='text-gray-600 w-6 h-6' />
              </button>
            </div>
          </div>
          <div className="py-6 px-6">
            <p className='text-center font-semibold text-lg text-gray-700 mb-6'>
              Are you sure you want to delete this attendance record?
            </p>
            <div className="space-x-3 text-center">
              <button
                disabled={deleteAttendance.isLoading}
                className='px-6 py-2 rounded-md font-semibold bg-red-200 text-red-700 hover:bg-red-300'
                onClick={handleDelete}
              >
                {deleteAttendance.isLoading ? "Deleting..." : "Yes, Delete"}
              </button>
              <button
                className='px-6 py-2 rounded-md font-semibold border border-gray-300 hover:bg-gray-100'
                onClick={() => setDeleteConfirmationModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </Layout>
  )
}

export default ViewMonthlyAttendance
